import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authService from '../services/auth';
import mfaService, { isPasskeyAlreadyHere, passkeyErrorMessage, passkeyUnavailableReason } from '../services/mfa';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';

interface LocationState {
  mfaTicket: string;
  availableMethods: { totp: boolean; email: boolean; passkey: boolean };
  rememberMe: boolean;
  from?: string;
}

type Mode = 'chooser' | 'totp' | 'email' | 'passkey' | 'offer-passkey';

type VerifiedLogin = Awaited<ReturnType<typeof mfaService.verifyEmail>>;

// "Not now" on the passkey offer is remembered per browser, so someone who
// signs in by email on purpose is asked once, not at every sign-in.
const OFFER_DISMISSED_KEY = 'mfa_passkey_offer_dismissed';

function offerDismissed(): boolean {
  try { return localStorage.getItem(OFFER_DISMISSED_KEY) === '1'; } catch { return false; }
}

function deviceNickname(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android phone';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Macintosh/.test(ua)) return 'Mac';
  return 'My device';
}

function MfaChallenge() {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeMfaLogin } = useAuth();
  const state = location.state as LocationState | null;

  const [mode, setMode] = useState<Mode>('chooser');
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  // Signed in by emailed code and being offered a passkey for this device.
  // The session token is already stored; the app is entered once they choose.
  const [signedIn, setSignedIn] = useState<VerifiedLogin | null>(null);
  const [offerOutcome, setOfferOutcome] = useState<'added' | 'already-here' | 'signed-out' | null>(null);

  // Auto-initiate passkey if available and user picks passkey
  useEffect(() => {
    if (mode === 'passkey' && state) {
      runPasskey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  if (!state?.mfaTicket) {
    return <Navigate to="/login" replace />;
  }

  const { mfaTicket, availableMethods, rememberMe } = state;
  const redirectTo = state.from || '/dashboard';

  const passkeyUnavailable = passkeyUnavailableReason();
  const canUsePasskey = availableMethods.passkey && !passkeyUnavailable;

  const enterApp = async (result: VerifiedLogin) => {
    await completeMfaLogin(result.user);
    navigate(redirectTo, { replace: true });
  };

  const finishSuccess = async (result: VerifiedLogin, method: 'totp' | 'email' | 'passkey') => {
    authService.completeMfaLogin(result.user, result.accessToken, rememberMe, result);
    // An emailed code on an account that uses passkeys usually means this
    // device holds none of them. Offer to make one here before going in.
    if (method === 'email' && canUsePasskey && !offerDismissed()) {
      setSignedIn(result);
      setCode('');
      setMode('offer-passkey');
      return;
    }
    await enterApp(result);
  };

  async function addPasskeyHere() {
    if (!signedIn) return;
    setError(null);
    setIsBusy(true);
    try {
      const stillSignedIn = await mfaService.registerPasskey(deviceNickname());
      if (stillSignedIn) {
        setOfferOutcome('added');
      } else {
        // A server from before sessions were kept: adding the passkey ended
        // this session with the others.
        setOfferOutcome('signed-out');
      }
    } catch (err) {
      if (isPasskeyAlreadyHere(err)) {
        setOfferOutcome('already-here');
      } else {
        setError(passkeyErrorMessage(err, 'The passkey could not be added.'));
      }
    } finally {
      setIsBusy(false);
    }
  }

  function skipPasskeyOffer() {
    try { localStorage.setItem(OFFER_DISMISSED_KEY, '1'); } catch { /* private mode */ }
    if (signedIn) void enterApp(signedIn);
  }

  async function runTotp() {
    setError(null);
    setIsBusy(true);
    try {
      const res = await mfaService.verifyTotp(mfaTicket, code, { trustDevice, rememberMe });
      await finishSuccess(res, 'totp');
    } catch (err: any) {
      setError(err?.message || 'Incorrect code');
    } finally {
      setIsBusy(false);
    }
  }

  async function sendEmailCode() {
    setError(null);
    setIsBusy(true);
    try {
      await mfaService.sendEmailChallenge(mfaTicket);
      setEmailSent(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send code');
    } finally {
      setIsBusy(false);
    }
  }

  async function runEmail() {
    setError(null);
    setIsBusy(true);
    try {
      const res = await mfaService.verifyEmail(mfaTicket, code, { trustDevice, rememberMe });
      await finishSuccess(res, 'email');
    } catch (err: any) {
      setError(err?.message || 'Incorrect code');
    } finally {
      setIsBusy(false);
    }
  }

  async function runPasskey() {
    setError(null);
    setIsBusy(true);
    try {
      const res = await mfaService.verifyPasskey(mfaTicket, { trustDevice, rememberMe });
      await finishSuccess(res, 'passkey');
    } catch (err: any) {
      // Cancelled, or this device holds none of the account's passkeys: the
      // browser reports both the same way. Back to the chooser, where the
      // emailed code is.
      if (err?.name === 'NotAllowedError' || err?.message?.includes('cancel')) {
        setMode('chooser');
        setError('No passkey was used. If this device does not have yours, choose "Email me a code".');
      } else {
        setError(err?.message || 'Passkey authentication failed');
      }
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-neutral-50 via-white to-primary-50">
      <div className="w-full max-w-md space-y-8">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">
              {mode === 'offer-passkey' ? 'Add a passkey to this device?' : 'Two-factor authentication'}
            </CardTitle>
            <CardDescription>
              {mode === 'offer-passkey' ? "You're signed in" : "Confirm it's you to finish signing in"}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {error && (
              <div className="rounded-lg bg-error-50 border border-error-200 p-3 mb-4 text-sm text-error-800">
                {error}
              </div>
            )}

            {mode === 'chooser' && (
              <div className="space-y-3">
                {canUsePasskey && (
                  <Button className="w-full" size="lg" onClick={() => setMode('passkey')}>
                    Use passkey
                  </Button>
                )}
                {availableMethods.totp && (
                  <Button
                    className="w-full"
                    size="lg"
                    variant={canUsePasskey ? 'outline' : 'primary'}
                    onClick={() => setMode('totp')}
                  >
                    Use authenticator app
                  </Button>
                )}
                {availableMethods.email && (
                  <Button
                    className="w-full"
                    size="lg"
                    variant={canUsePasskey || availableMethods.totp ? 'outline' : 'primary'}
                    onClick={() => setMode('email')}
                  >
                    Email me a code
                  </Button>
                )}
                {availableMethods.passkey && availableMethods.email && (
                  <p className="text-sm text-neutral-600 pt-1">
                    {passkeyUnavailable
                      ?? 'No passkey on this device? Email yourself a code, and you can add one here once you are in.'}
                  </p>
                )}
              </div>
            )}

            {mode === 'offer-passkey' && (
              <div className="space-y-4">
                {offerOutcome === null && (
                  <>
                    <p className="text-sm text-neutral-600">
                      Your account uses passkeys, and you signed in here with an emailed code. Add a passkey
                      to this device and next time it is a fingerprint, face or PIN instead.
                    </p>
                    <Button className="w-full" size="lg" isLoading={isBusy} onClick={addPasskeyHere}>
                      Add a passkey to this device
                    </Button>
                    <Button className="w-full" size="lg" variant="outline" disabled={isBusy} onClick={skipPasskeyOffer}>
                      Not now
                    </Button>
                  </>
                )}
                {offerOutcome === 'added' && (
                  <>
                    <p className="text-sm text-neutral-600">
                      Passkey added. Your other devices have been signed out, and each can sign back in
                      with its own passkey or an emailed code.
                    </p>
                    <Button className="w-full" size="lg" onClick={() => signedIn && enterApp(signedIn)}>
                      Continue
                    </Button>
                  </>
                )}
                {offerOutcome === 'already-here' && (
                  <>
                    <p className="text-sm text-neutral-600">
                      This device already has a passkey for your account, most likely from a password
                      manager that syncs to it. There is nothing to add: choose "Use passkey" next time
                      you sign in.
                    </p>
                    <Button className="w-full" size="lg" onClick={() => signedIn && enterApp(signedIn)}>
                      Continue
                    </Button>
                  </>
                )}
                {offerOutcome === 'signed-out' && (
                  <>
                    <p className="text-sm text-neutral-600">
                      Passkey added. Sign in once more to finish, and choose "Use passkey".
                    </p>
                    <Button className="w-full" size="lg" onClick={() => navigate('/login', { replace: true })}>
                      Sign in
                    </Button>
                  </>
                )}
              </div>
            )}

            {mode === 'totp' && (
              <div className="space-y-4">
                <p className="text-sm text-neutral-600">
                  Enter the 6-digit code from your authenticator app.
                </p>
                <Input
                  label="Code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                />
                <TrustCheckbox trustDevice={trustDevice} setTrustDevice={setTrustDevice} />
                <Button className="w-full" size="lg" isLoading={isBusy} onClick={runTotp} disabled={code.length !== 6}>
                  Verify
                </Button>
                <BackLink onBack={() => { setMode('chooser'); setCode(''); setError(null); }} />
              </div>
            )}

            {mode === 'email' && (
              <div className="space-y-4">
                {!emailSent ? (
                  <>
                    <p className="text-sm text-neutral-600">
                      We'll email you a 6-digit code to finish signing in.
                    </p>
                    <Button className="w-full" size="lg" isLoading={isBusy} onClick={sendEmailCode}>
                      Send code
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-neutral-600">
                      Check your inbox and enter the 6-digit code below.
                    </p>
                    <Input
                      label="Code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                    />
                    <TrustCheckbox trustDevice={trustDevice} setTrustDevice={setTrustDevice} />
                    <Button className="w-full" size="lg" isLoading={isBusy} onClick={runEmail} disabled={code.length !== 6}>
                      Verify
                    </Button>
                  </>
                )}
                <BackLink onBack={() => { setMode('chooser'); setCode(''); setEmailSent(false); setError(null); }} />
              </div>
            )}

            {mode === 'passkey' && (
              <div className="space-y-4 text-center">
                <p className="text-sm text-neutral-600">
                  Follow the prompt from your device to sign in with your passkey.
                </p>
                <Button className="w-full" size="lg" isLoading={isBusy} onClick={runPasskey}>
                  Try again
                </Button>
                <BackLink onBack={() => { setMode('chooser'); setError(null); }} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function TrustCheckbox({ trustDevice, setTrustDevice }: { trustDevice: boolean; setTrustDevice: (b: boolean) => void }) {
  return (
    <label className="flex items-center text-sm text-neutral-700 select-none">
      <input
        type="checkbox"
        checked={trustDevice}
        onChange={(e) => setTrustDevice(e.target.checked)}
        className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 mr-2"
      />
      Trust this device for 30 days
    </label>
  );
}

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="w-full text-sm text-primary-600 hover:text-primary-700 transition-colors"
    >
      ← Choose a different method
    </button>
  );
}

export default MfaChallenge;
